"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function InitiateYourSupport() {
  return <section data-section-id="1684" id="get-started-with-cncwise" className="bg-background py-24 text-foreground">
      <div className="container mx-auto max-w-lg px-6">
        <h2 className="mb-16 text-center text-5xl font-extrabold tracking-tighter text-foreground">Contact Form</h2>

        <p className="text-center text-muted-foreground mb-8">
          Whether you're looking for more information about the CNCWise software platform, looking to begin a component repair inquiry, looking to inquire about partnering as an OEM, or looking for assistance with billing or general platform help, fill out this form and one of our team members will reach out to you shortly. We reply to most inquiries within two hours.
        </p>

        <FormProvider formId="cncwise_contact_form" sectionName="get-started-with-cncwise" defaultValues={{
        name: "",
        email: "",
        phone: "",
        company: "",
        support_type: "",
        details: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: false
        },
        company: {
          required: true
        },
        support_type: {
          required: true
        },
        details: {
          required: true
        }
      }} submitText="Connect with Experts" successMessage="Inquiry received. A member of our team will reach out to you shortly.">
          <SupportForm />
        </FormProvider>
      </div>
    </section>;
}
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-l-4 border-primary bg-muted/50 p-8 text-center shadow-lg">
        <h3 className="mb-2 text-2xl font-bold tracking-tight text-primary">
          Request Logged
        </h3>
        <p className="text-muted-foreground font-medium">{successMessage}</p>
      </div>;
  }
  const inputClasses = "border-0 border-b border-border bg-transparent px-0 py-2 text-base shadow-none rounded-none focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/40 h-auto transition-all duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Enter your name" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Company Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="example@cncshop.com" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Direct Phone <span className="text-[10px] opacity-60">(Optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Company Name
            </FormLabel>
            <FormControl>
              <Input placeholder="XYZ Machine Shop" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="support_type" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Inquiry Category
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className={`${inputClasses} w-full justify-between font-normal`}>
                  <SelectValue placeholder="Select interest area" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="AI Troubleshooting">
                  CNCWise Software Platform Inquiry
                </SelectItem>
                <SelectItem value="Maintenance Scheduling">
                  Component Repair Inquiry
                </SelectItem>
                <SelectItem value="Electronic Repair Discount">
                  OEM Partnership Inquiry
                </SelectItem>
                <SelectItem value="General Technical Inquiry">
                  Billing or Support Inquiry
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" rules={{
      required: "Please provide some details"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Details
            </FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Describe how we can assist you..." className={`${inputClasses} min-h-[100px] w-full resize-y bg-transparent outline-none`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="bg-destructive/10 border-l-2 border-destructive p-4 text-sm font-medium text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="mt-8 h-14 w-full rounded-sm bg-primary text-lg font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_25px_rgba(34,197,94,0.5)] transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Synchronizing...
          </> : "Initiate Request"}
      </Button>
    </Form>;
}